<template>
    <vx-expandable-card toggleable id="what" :active="active">
        <template v-slot:header>
            <div class="flex items-center pb-1 lg:pb-0">
                <span
                    class="pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                    :class="{
                        'border-r-transparent': !showStats,
                        'border-r-slate-400': showStats,
                    }"
                >
                    What
                </span>
                <div v-if="showStats" class="flex gap-4">
                    <div v-for="data in metadata" :key="data.label" class="flex flex-col">
                        <span class="font-bold text-xs text-slate-700 block sm:text-base sm:font-semibold">
                            {{ data.value }}
                        </span>
                        <span class="text-xs font-normal text-slate-500 block">
                            <span class="hidden sm:inline">{{ data.label }}</span>
                            <span class="inline sm:hidden">{{ data.shortLabel }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </template>
        <template v-if="editable && !props.success" v-slot:headerRight>
            <vx-button @click.stop="emit('update:active', true)" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-slot:content>
            <strong class="text-slate-900 text-sm font-semibold mb-2 block sm:text-base"> Message </strong>
            <input-with-errors :errors="props.errors" autoFocus>
                <template v-slot:alert="{ error }">
                    <vx-alert color="danger" :closeable="false">
                        <template v-slot:icon>
                            <font-awesome-icon :icon="faTriangleExclamation" widthAuto></font-awesome-icon>
                        </template>
                        {{ error }}
                        <template v-slot:right>
                            <vx-badge size="2xs" class="ml-1 whitespace-nowrap" color="danger">
                                Set by Corp <font-awesome-icon :icon="faCrown" />
                            </vx-badge>
                        </template>
                    </vx-alert>
                </template>

                <message-builder
                    ref="messageBuilderRef"
                    v-model="data.body"
                    v-model:mediaUrl="data.mediaUrl"
                    v-model:mediaContentType="data.mediaContentType"
                    v-model:snippetId="data.snippetId"
                    v-model:snippetFieldBindings="data.snippetFieldBindings"
                    @selected:snippet="emit('snippetUsed', $event)"
                    preview
                    :externalContactLists="props.externalContactLists"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    :onlyLockedSnippetsAllowed="onlyLockedSnippetsAllowed"
                    :disableLockedSnippets="props.campaignType === AUDIENCE_TYPE_TRANSACTIONAL"
                >
                    <template v-if="FEATURES.quick_blast_ai && !onlyLockedSnippetsAllowed" v-slot:actions>
                        <vx-dropdown-item @click.prevent="aiModal = true">
                            <span class="flex items-center gap-1">
                                <font-awesome-icon
                                    widthAuto
                                    :icon="faWandMagicSparkles"
                                    class="text-sm"
                                ></font-awesome-icon>
                                <span>AI Message</span>
                                <experimental-pill class="pointer-events-none" placement="right"></experimental-pill>
                            </span>
                        </vx-dropdown-item>
                    </template>
                </message-builder>
            </input-with-errors>

            <message-builder-quick-blast-generation
                v-if="FEATURES.quick_blast_ai && !onlyLockedSnippetsAllowed"
                v-model:visible="aiModal"
                :campaign-type="props.campaignType"
                @selected="data.body = $event"
            ></message-builder-quick-blast-generation>

            <vx-button
                @click="confirm()"
                data-test="what-continue"
                class="mt-4 lg:mt-6"
                size="lg"
                :block="true"
                :loading="loading"
                :disabled="!data.body || messageBuilderRef?.snippetLoading"
            >
                Continue
                <font-awesome-icon widthAuto class="text-base" :icon="faArrowTurnDown"></font-awesome-icon>
            </vx-button>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxButton, VxDropdownItem, VxAlert, VxBadge } from '@voxie/frontend-components';
import { computed, inject, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPencil, faArrowTurnDown, faTriangleExclamation, faCrown } from '@fortawesome/pro-solid-svg-icons';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import InputWithErrors from '~/components/general/InputWithErrors.vue';
import MessageBuilderQuickBlastGeneration from '~/components/general/message-builder/modals/MessageBuilderQuickBlastGeneration.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';
import ExperimentalPill from '~/components/general/ExperimentalPill.vue';
import {
    UNICODE_TO_GSM,
    GSM_MAX_SINGLE_SEGMENT_SIZE,
    GSM_MAX_CONCAT_SEGMENT_SIZE,
    UCS2_MAX_SINGLE_SEGMENT_SIZE,
    UCS2_MAX_CONCAT_SEGMENT_SIZE,
} from '~/support/GSM';
import { useToasted } from '~/composables/useToasted';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import useVuelidate from '@vuelidate/core';

const toasted = useToasted();
const FEATURES = inject('FEATURES');

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    active: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    campaignType: {
        type: String,
        required: true,
        validator: (campaignType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(campaignType),
    },
    externalContactLists: {
        type: Array,
        default: () => [],
    },
    errors: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['update:modelValue', 'update:active', 'continue', 'snippetUsed']);

const { shouldBlockImages, shouldBlockFiles, lockedSnippetsEnabled } = useMessageRules();
const validation = useVuelidate();

const aiModal = ref(false);
const messageBuilderRef = ref(null);

const data = defineModel({
    body: '',
    mediaUrl: null,
    mediaContentType: null,
    snippetId: null,
    snippetFieldBindings: [],
});

const editable = computed(() => Boolean(!props.active && data.value.body));
const showStats = computed(() => Boolean(props.active || data.value.body));

const onlyLockedSnippetsAllowed = computed(() => lockedSnippetsEnabled(props.campaignType));
const blockImages = computed(() => shouldBlockImages(props.campaignType));
const blockFiles = computed(() => shouldBlockFiles(props.campaignType));

const confirm = async () => {
    if (!data.value.body.length) {
        toasted.error('Enter a message.');
        return;
    }

    if (onlyLockedSnippetsAllowed.value && (data.value.snippetFieldBindings || []).length > 0) {
        const isValid = await validation.value.$validate();

        if (!isValid) {
            return;
        }
    }

    emit('continue');
};

const encoding = computed(() => (bodyCodePoints.value.every((charDec) => charDec in UNICODE_TO_GSM) ? 'GSM' : 'UCS2'));

const messageType = computed(() => (data.value.mediaUrl ? 'MMS' : 'SMS'));

const bodyCodePoints = computed(() => (data.value.body?.split('') || []).map((char) => char.codePointAt(0) ?? 0));

const bodyGSMEncodedLength = computed(() =>
    bodyCodePoints.value.reduce((total, charDec) => (total += UNICODE_TO_GSM[charDec]?.length ?? 0), 0)
);

const segmentsCount = computed(() => {
    if (messageType.value === 'MMS') {
        return undefined;
    }

    if ((data.value.body?.length ?? 0) === 0) {
        return 0;
    }

    if (encoding.value === 'GSM') {
        return bodyGSMEncodedLength.value <= GSM_MAX_SINGLE_SEGMENT_SIZE
            ? 1
            : Math.ceil(bodyGSMEncodedLength.value / GSM_MAX_CONCAT_SEGMENT_SIZE);
    }

    if (encoding.value === 'UCS2') {
        if (data.value.body.length <= UCS2_MAX_SINGLE_SEGMENT_SIZE) {
            return 1;
        }

        let subTotal = 0;
        const segments = (data.value.body?.split('') || [])
            .map((char) => char.length)
            .reduce((acc, charLength) => {
                subTotal += charLength;
                if (subTotal <= UCS2_MAX_CONCAT_SEGMENT_SIZE) {
                    return acc;
                }

                subTotal = charLength;
                return ++acc;
            }, 1);

        return segments;
    }

    return 0;
});

const metadata = computed(() => {
    const items = [
        {
            label: 'message',
            shortLabel: 'msg',
            value: messageType.value,
        },
    ];

    if (segmentsCount.value !== undefined) {
        items.push({
            label: 'segments',
            shortLabel: 'segs',
            value: segmentsCount.value,
        });
    }

    items.push({
        label: 'characters',
        shortLabel: 'chars',
        value: data.value.body?.length ?? 0,
    });

    return items;
});
</script>
